import React, { useContext } from 'react';

import { ReliabilityCalculatorContext } from './Context';
import { Data } from '../../components';

const ReliabilityArticles = () => {
  const {
    state: { step2, step3, step4, step5, step6, step7, step8, step9 },
  } = useContext(ReliabilityCalculatorContext);

  const getArticles = () => {
    const articles = [];

    if (step2 === 0) {
      articles.push({
        heading: 'Estimate your MTBF',
        blurb: `Keeping a close eye on availbility KPIs like mean-time-between-failure (MTBF) is important to understanding where you should focus engineering effort. You can begin to estimate MTBF by dividing the total hours in a given period by the number of incidents that occured within that same time frame. We dive deeper into which metrics matter most in <a href="https://www.gremlin.com/community/tutorials/chaos-engineering-monitoring-metrics-guide/">this tutorial</a>.`,
      });
    }

    if (step3 === 0) {
      articles.push({
        heading: 'Measure your MTTR and MTTD',
        blurb: `Measuring mean-time-to-recover (MTTR) can help your team understand not only the severity of incidents that impact this service but also the health of your incident response. For example, by comparing mean-time-to-detect (MTTD) and mean-time-to-recover (MTTR), you can uncover whether your monitoring and alerting is properly configured to detect out-of-range metrics and quickly engage the right team members. For mor information on optimizing MTTD and MTTR, we recommend "Reducing MTTD for High-Severity Incidents" from O'Reilly. You can get your copy <a href="https://www.gremlin.com/oreilly-reducing-mttd-for-high-severity-incidents/">here</a>.`,
      });
    }

    if (step4 === 0) {
      articles.push({
        heading: 'Define availability SLOs',
        blurb: `Establishing an availability SLO for Tier 1 and Tier 2 services is critical for understanding whether additional focus is required on improving reliability or whether release cycles can continue unaffected. This is particularly useful if you have existing availability SLAs that you communicate to your users. You can learn more about setting SLOs in <a href="https://www.gremlin.com/community/tutorials/how-to-establish-a-high-severity-incident-management-program/">this blog post</a>.`,
      });
    }

    if (step5 === 'no') {
      articles.push({
        heading: 'Document and test your incident response procedures',
        blurb: `Establishing procedures to follow in the event of an incident is a core concept in modern incident management. Often referred to as "runbooks" or "playbooks," these resources reduce the mental load required of your team during high-stress incident response situations. Additionally, you can use Chaos Engineering to run "FireDrills" that not only ensure your runbooks are up to date but also to ensure your team is properly trained for a real outage scenario. See how this is done in <a href="https://www.gremlin.com/blog/ensuring-runbooks-are-up-to-date/">this post</a>.`,
      });
    }

    if (step6 === 'no') {
      articles.push({
        heading: 'Consider setting error budgets for Tier 1 services',
        blurb: `Closely related to SLOs, error budgets provide valuable insight into where engineering efforts should be focused. Particularly for Tier 1 services, error budgets indicate whether it is safe or prudent to continue pushing new releases or whether engineering priorities should be redirected to reliability efforts. Learn more about the relationship between error budgets and SLOs <a href="https://landing.google.com/sre/sre-book/chapters/embracing-risk/">in Google's SRE Book</a>.`,
      });
    }

    if (step7 === 'no' || step7 === 'yes-business') {
      articles.push(
        {
          heading: 'Assign IMOCs and establish an on-call rotation',
          blurb: `Setting up an on-call rotation can dramatically reduce your MTTD (mean time-to-detect) and MTTR (mean time-to-resolve) for severe incidents. Assigning your best engineers to on-call rotations ensures that, in the event of an incident, there is a clear point person for managing the incident response (sometimes called a "call leader" or "incident manager on-call" (IMOC)) who is familiar with the impacted service. We provide an example IMOC rotation schedule in <a href="https://www.gremlin.com/community/tutorials/understanding-the-role-of-the-incident-manager-on-call-imoc/#how-do-you-create-an-effective-imoc-rotation">this tutorial</a>.`,
        },
        {
          heading: 'Expand your on-call rotation',
          blurb:
            'Extending the scope of your on-call rotations can improve MTTD and MTTR should incidents occur outside of business hours. Additionally, it further incentivizes improving the reliability of that service.',
        }
      );
    }

    if (step8 === 'no' || step8 === 'yes-monitoring') {
      articles.push(
        {
          heading: 'Establish observability',
          blurb: `Monitoring your service is a fundamental requirement not just for Chaos Engineering but for any type of reliability optimization. Very simply, you cannot optimize what you cannot measure. We discuss how to think about what metrics to start monitoring in the <a href="https://www.gremlin.com/chaos-engineering/stage-0/">Stage 0 section of our staged resiliency guide</a>.`,
        },
        {
          heading: 'Consider setting alerting thresholds',
          blurb:
            'If you have determined an availability SLO (or you have determined that this service is a dependency for a service that does), consider setting up alerting thresholds on several KPIs. Letting your engineers know when metrics like error rates, latency, or even number of requests are out of expected range can dramatically reduce the chance of a severe incident.',
        }
      );
    }

    if (step9 === 'never') {
      articles.push({
        heading: 'Run your first Chaos Experiment',
        blurb: `Injecting failure into your service for the first time may seem daunting. However, Chaos Engineering is all about designing thoughtful, disciplined experiments to maximize your learning and minimize risk. We provide examples of how to start running your first Chaos Experiments in <a href="https://www.gremlin.com/chaos-engineering/stage-1/">Stage 1 of our staged resiliency guide</a>.`,
      });
    }

    if (step9 === 'annually') {
      articles.push({
        heading: 'Moving beyond annual experimentation',
        blurb:
          'Your annual experiments should focus on measuring the reliability of your most important (Tier 1) services, but each time you update these services you introduce changes that might impact the rest of the system. Running more frequent Chaos Experiements is a proactive way to catch potential issues before they become outages. Additionally, you can consider dialing up the blast radius (host count), the duration, and the magnitude of your attacks to test the resilience of your services.',
      });

      if (step9 === 'quarterly') {
        articles.push({
          heading: 'Moving from quarterly to monthly experimentation',
          blurb:
            'Running quarterly experiments is the start of a minimum viable Chaos Engineering practice, but running chaos experiments monthly will dramatically increase your ROI. There are a couple of ways we recommend teams gradually increase their efforts. First, consider how often you update your services and establish baseline Chaos Experiments to run with each deploy. This prevents the drift into failure. Additionally,  consider dialing up the blast radius (host count), the duration, and the magnitude of your attacks to learn how reliable your services are.',
        });
      }

      if (step9 === 'monthly') {
        articles.push({
          heading: 'Moving from monthly to weekly experimentation',
          blurb:
            'Running monthly experiments sets your company apart from most, and you’ll see the benefits of more reliable services and teams that understand how their systems truly operate. Your next steps should be to consider automating a set of chaos experiments to run with each deploy. This prevents the drift into failure. Additionally, consider GameDays with your team where you dial up the blast radius (host count), the duration, and the magnitude of your attacks to learn how reliable your services are.',
        });
      }

      if (step9 === 'weekly') {
        articles.push({
          heading: 'Moving from weekly to fully automated experimentation',
          blurb:
            'If you’re running weekly chaos experiments, you’ve made reliability a priority. Your next steps should be to consider which experiments you haven’t automate to run with each deploy to prevent the drift into failure. We also recommend you begin performing regular disaster recovery tests by running Black hole experiments on services to see how the system responds. Finally, share your work with the rest of your company to demonstrate the value of these efforts. ',
        });
      }
    }

    return articles;
  };

  return getArticles().length > 0 ? (
    <ul className="reliabilityCalculator__recommended-articles u-contrast">
      {getArticles().map(article => (
        <li key={article.heading}>
          <article className="reliabilityCalculator__recommended-article">
            <h3>{article.heading}</h3>
            <hr
              className="reliabilityCalculator__introCopy-hr"
              style={{ width: '100px' }}
            />
            <p>
              <Data>{article.blurb}</Data>
            </p>
          </article>
        </li>
      ))}
    </ul>
  ) : null;
};

export default ReliabilityArticles;
